import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {Dispatch, SetStateAction, useEffect, useState} from 'react';
import isEqual from 'react-fast-compare';

import {updateBenchlingAppInstance} from 'benchling-apps/BenchlingAppDAO';
import {BenchlingAppType} from 'benchling-apps/BenchlingAppTypes';
import EmptyStateCard from 'benchling-apps/EmptyStateCard';
import FeatureConfigurationBlock from 'benchling-apps/interactivity/features/FeatureConfigurationBlock';
import {ResourceFeatureAssociation} from 'benchling-apps/interactivity/features/types';
import {BenchlingAppFeatureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import notifications from 'notifications';
import {FONT_FAMILY_PROXIMA} from 'styles/FontConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {TooltipWrapper} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY, GRAY_LIGHT, GRAY_DARKER} from 'ui-library/Colors';
import {Header1} from 'ui-library/Headers';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {
  getBenchlingAppConfigurationQuery,
  getBenchlingAppConfigurationQueryVariables,
} from './__generated/getBenchlingAppConfigurationQuery';
import {GetResourceFeatureAssociations} from './__generated/GetResourceFeatureAssociations';
import {getNumInvalidSubdeps} from './BenchlingAppConfigurationRow';
import BenchlingAppConfigurationRowSet from './BenchlingAppConfigurationRowSet';
import {benchlingAppConfigurationBlockState} from './hooks/useGlobalConfigValuesState';
import {
  ArrayElementsValidation,
  ArrayElementValidation,
  UpdateArrayElementValidationCallback,
  ConfigurationsValidation,
  ConfigValidationErrorTypes,
  DependencyConfigValidation,
} from './validation/ValidationTypes';

type Feature = ExtractByTypename<
  getBenchlingAppConfigurationQuery['benchlingApp'],
  'BenchlingAppNode'
>['features'][number];

type Configuration = ExtractByTypename<
  getBenchlingAppConfigurationQuery['benchlingApp'],
  'BenchlingAppNode'
>['configuration'][number];

type ArrayElementConfig = NonNullable<Configuration['elements']>[number]['elementConfigs'][number];

export const BENCHLING_APP_CONFIGURATION_QUERY = gql`
  query getBenchlingAppConfigurationQuery($benchlingAppId: StrictString!)
  @skipAudit
  @engComponent(name: BENCHLING_APPS) {
    benchlingApp(id: $benchlingAppId) {
      ... on BenchlingAppNode {
        id
        name
        isConfigurationTabVisible
        isMissingRequiredPublicKey
        configuration {
          ...BenchlingAppConfigurationGraphQL
          defaultElementName
          minElements
          maxElements
          elementDefinition {
            ...BenchlingAppConfigurationGraphQL
          }
          elements {
            name
            elementConfigs {
              ...BenchlingAppConfigurationGraphQL
            }
          }
        }
        features {
          id
          name
          type
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('BenchlingAppConfiguration').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const RESOURCE_FEATURE_ASSOCIATIONS_QUERY = gql`
  query GetResourceFeatureAssociations @skipAudit @engComponent(name: BENCHLING_APPS) {
    benchlingAppResourceFeatureAssociations {
      ...ResourceFeatureAssociationGraphQL
    }
  }
  ${getFragmentWrapper('ResourceFeatureAssociation').fragment}
`;

const styles = StyleSheet.create({
  configurations: {
    padding: '16px 24px',
    borderBottom: `1px solid ${GRAY_LIGHT}`,
  },
  emptyConfigurationsHeader: {
    padding: '16px 24px',
    borderBottom: `1px solid ${GRAY_LIGHT}`,
    marginBottom: '0px',
  },
  stickyButton: {
    height: '100%',
    marginTop: '20px',
    position: 'sticky',
    left: '200%',
  },
  configCard: {
    marginTop: '10px',
    overflow: 'scroll',
    paddingRight: '10px',
    '::-webkit-scrollbar': {
      '-webkit-appearance': 'none',
      width: '7px',
    },
    '::-webkit-scrollbar-thumb': {
      'border-radius': '4px',
      'background-color': GRAY,
    },
  },
  headerStyle: {
    fontFamily: FONT_FAMILY_PROXIMA,
    fontStyle: 'normal',
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: '16px',
  },
  helperText: {
    color: GRAY_DARKER,
  },
  publicKeyWarningBanner: {
    marginTop: '15px',
  },
});

export const initializeConfigValidation = (
  configs: Array<Configuration | ArrayElementConfig>
): ConfigurationsValidation => {
  const configValidation: ConfigurationsValidation = {};

  configs.forEach((config) => {
    const depName = config.name;
    configValidation[depName] = {
      errors: [],
      subdependencyValidation: {},
      outputSubdependencyValidation: {},
    };

    config.fieldDefinitions?.forEach((fieldDefinition) => {
      const subdepName = fieldDefinition.name;
      configValidation[depName].subdependencyValidation[subdepName] = {
        errors: [],
      };
    });

    config.output?.fieldDefinitions.forEach((fieldDefinition) => {
      const subdepName = fieldDefinition.name;
      configValidation[depName].outputSubdependencyValidation[subdepName] = {
        errors: [],
      };
    });

    config.options?.forEach((option) => {
      const subdepName = option.name;
      configValidation[depName].subdependencyValidation[subdepName] = {
        errors: [],
      };
    });
  });

  return configValidation;
};

// Held in an object so tests can stub the initializer the section below uses.
export const configValidationInitializer = {initializeConfigValidation};

const getTooltipMessage = (
  configsValidation: ConfigurationsValidation,
  arrayElementsValidation: ArrayElementsValidation,
  isMissingPublicKey: boolean
): string => {
  if (isMissingPublicKey) {
    return 'Cannot submit configuration; missing public key.';
  }

  const allValidations: Array<DependencyConfigValidation> = Object.values(configsValidation);

  for (const arrayElementValidation of Object.values(arrayElementsValidation)) {
    if (arrayElementValidation != null) {
      allValidations.push(...Object.values(arrayElementValidation.validation));
    }
  }

  const isMissingRequiredConfig = allValidations.some((dependencyValidation) =>
    hasRequiredConfigError(dependencyValidation)
  );

  const duplicateArrayNameError = getDuplicateArrayNameError(configsValidation);

  const numberOfElementsError = getNumberOfElementsError(configsValidation);

  if (isMissingRequiredConfig) {
    return 'Fill out all required configurations to save changes.';
  }

  if (duplicateArrayNameError) {
    return duplicateArrayNameError;
  }

  if (numberOfElementsError) {
    return `Some arrays have too many or too few elements. ${numberOfElementsError}`;
  }

  return '';
};

const hasRequiredConfigError = (dependencyValidation: DependencyConfigValidation): boolean => {
  const errors = Object.values(dependencyValidation.subdependencyValidation).reduce(
    (acc, subdependencyValidation) => acc.concat(subdependencyValidation.errors),
    dependencyValidation.errors
  );
  return errors.some((err) => err.type === ConfigValidationErrorTypes.RequiredConfigError);
};

const getDuplicateArrayNameError = (configsValidation: ConfigurationsValidation): string => {
  const errors: Array<string> = [];
  for (const dependencyValidation of Object.values(configsValidation)) {
    for (const e of dependencyValidation.errors) {
      if (e.type === ConfigValidationErrorTypes.DuplicateArrayNameError) {
        errors.push(e.message);
      }
    }
  }
  return errors.join('\n');
};

const getNumberOfElementsError = (configsValidation: ConfigurationsValidation): string => {
  const errors: Array<string> = [];
  for (const dependencyValidation of Object.values(configsValidation)) {
    for (const e of dependencyValidation.errors) {
      if (e.type === ConfigValidationErrorTypes.ConfigArrayNumberOfElementsError) {
        errors.push(e.message);
      }
    }
  }
  return errors.join('\n');
};

const hasConfigValidationErrors = (
  configsValidation: ConfigurationsValidation,
  arrayElementsValidation: ArrayElementsValidation
): boolean => {
  const allValidations: Array<DependencyConfigValidation> = Object.values(configsValidation);
  for (const arrayElementValidation of Object.values(arrayElementsValidation)) {
    if (arrayElementValidation != null) {
      allValidations.push(...Object.values(arrayElementValidation.validation));
    }
  }

  return Object.values(allValidations)
    .map((dependencyConfigValidation) => {
      // Check for dependency errors
      const hasErrors = dependencyConfigValidation.errors.length > 0;

      // Check for subdependency errors
      const subdepErrors = getNumInvalidSubdeps(dependencyConfigValidation);

      return hasErrors || subdepErrors > 0;
    })
    .reduce(
      (thisConfigHasErrors, prevConfigsHaveErrors) => thisConfigHasErrors || prevConfigsHaveErrors,
      false
    );
};

// We coerce null values to False for Boolean type configs
function _coerceBooleanValue(config: Configuration | ArrayElementConfig): Configuration | ArrayElementConfig {
  if (config.type === BenchlingAppType.boolean) {
    return {...config, value: config.value ?? false};
  }
  return config;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function convertConfigurationToUpdatePayload(configuration: Array<Configuration>): Array<any> {
  const convertedConfigs = [];
  for (const config of configuration) {
    let convertedElements = null;
    if (config.elements != null) {
      convertedElements = [];
      for (const element of config.elements) {
        const elementConfigs: Array<ArrayElementConfig> = element.elementConfigs.map((c) =>
          _coerceBooleanValue(c)
        );
        convertedElements.push({
          ...element,
          type: BenchlingAppType.array_element,
          elementConfigs,
        });
      }
    }

    convertedConfigs.push({
      ..._coerceBooleanValue(config),
      elements: convertedElements,
    });
  }
  return convertedConfigs;
}

function getResourceFeatureAssociationUpdatePayload(
  editedResourceFeatureAssociations: Array<ResourceFeatureAssociation>,
  appId: string
): Array<{feature: number; resources: Array<string>}> {
  let updatePayload: Array<{feature: number; resources: Array<string>}> = [];
  const featureIdsForApp = editedResourceFeatureAssociations
    .filter((rfa) => rfa.appSummary.id === appId)
    .map((rfa) => rfa.featureId);
  const uniqueFeatureIds = new Set(featureIdsForApp);
  for (const featureId of uniqueFeatureIds) {
    const associatedResourceIds = editedResourceFeatureAssociations
      .filter((rfa) => rfa.featureId === featureId)
      .map((rfa) => rfa.resourceId);
    updatePayload = [...updatePayload, {feature: featureId, resources: associatedResourceIds}];
  }
  return updatePayload;
}

export function BasicConfigurationBlock({
  configurations,
  editedConfiguration,
  updateEditedConfiguration,
  configsValidation,
  updateConfigsValidation,
  updateArrayElementValidation,
}: {
  configurations: Array<Configuration>;
  editedConfiguration: Array<Configuration>;
  updateEditedConfiguration: (newValue: Array<Configuration>) => void;
  configsValidation: ConfigurationsValidation;
  updateConfigsValidation: Dispatch<SetStateAction<ConfigurationsValidation>>;
  updateArrayElementValidation: UpdateArrayElementValidationCallback;
}): JSX.Element | null {
  return configurations.length === 0 ? (
    <></>
  ) : (
    <div className={classNames(css(styles.configurations))}>
      <div className={classNames(css(styles.headerStyle))}>Basic Configurations</div>
      <div className={classNames(css(styles.helperText))}>
        Fill out basic configurations for core app functionality.
      </div>
      <BenchlingAppConfigurationRowSet
        configuration={editedConfiguration}
        updateConfiguration={updateEditedConfiguration}
        configsValidation={configsValidation}
        updateConfigsValidation={updateConfigsValidation}
        updateArrayElementValidation={updateArrayElementValidation}
      />
    </div>
  );
}

function BenchlingAppConfigurationBlock({
  benchlingAppId,
  benchlingAppName,
  configurations,
  resourceFeatureAssociations,
  handleUpdateConfiguration,
  features,
  isMissingPublicKey,
}: {
  benchlingAppId: string;
  benchlingAppName: string;
  configurations: Array<Configuration>;
  resourceFeatureAssociations: Array<ResourceFeatureAssociation>;
  handleUpdateConfiguration: (
    newConfiguration: Array<Configuration>,
    newResourceFeatureAssociations: Array<{feature: number; resources: Array<string>}>
  ) => Promise<void>;
  features: Array<Feature>;
  isMissingPublicKey: boolean;
}): JSX.Element | null {
  const [editedConfiguration, updateEditedConfiguration] =
    benchlingAppConfigurationBlockState.useGlobalClientState(benchlingAppId);
  const [serverConfiguration, updateServerConfiguration] =
    benchlingAppConfigurationBlockState.useGlobalClientState(`server_${benchlingAppId}`);
  const [configsValidation, updateConfigsValidation] = useState(
    configValidationInitializer.initializeConfigValidation(editedConfiguration)
  );
  const [arrayElementsValidation, updateArrayElementsValidation] = useState<ArrayElementsValidation>({});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [editedResourceFeatureAssociations, updateResourceFeatureAssociations] =
    useState<Array<ResourceFeatureAssociation>>(resourceFeatureAssociations);
  useEffect(() => {
    if (!isEqual(configurations, serverConfiguration)) {
      updateServerConfiguration(configurations);
      updateEditedConfiguration(configurations);
      updateConfigsValidation(configValidationInitializer.initializeConfigValidation(configurations));
    }
  }, [
    configurations,
    updateEditedConfiguration,
    editedConfiguration,
    updateServerConfiguration,
    serverConfiguration,
  ]);
  const basicConfigurationIsEdited = !isEqual(configurations, editedConfiguration);
  const resourceFeatureAssociationsAreEdited = !isEqual(
    resourceFeatureAssociations,
    editedResourceFeatureAssociations
  );

  function updateArrayElementValidation(
    arrayElementKey: string,
    newArrayElementValidation: ArrayElementValidation | null
  ): void {
    updateArrayElementsValidation({
      ...arrayElementsValidation,
      [arrayElementKey]: newArrayElementValidation,
    });
  }

  return (
    <>
      <div className={classNames('u-spacing-top', css(styles.configurations))}>
        <div className="u-lr-split">
          <div>
            <Header1>Configurations</Header1>
            <div className={classNames(css(styles.helperText))}>
              Fill in the fields below. Submit changes to map your data to "{benchlingAppName}" and complete
              its setup.
            </div>
          </div>
          <TooltipWrapper
            tooltipTitle={getTooltipMessage(configsValidation, arrayElementsValidation, isMissingPublicKey)}
          >
            <Button
              buttonStyle={ButtonStyle.SECONDARY}
              size={ButtonSize.SMALL}
              text={isSubmitting ? 'Submitting...' : 'Submit'}
              disabled={
                !(basicConfigurationIsEdited || resourceFeatureAssociationsAreEdited) ||
                isSubmitting ||
                hasConfigValidationErrors(configsValidation, arrayElementsValidation) ||
                isMissingPublicKey
              }
              onClick={async () => {
                setIsSubmitting(true);
                try {
                  await handleUpdateConfiguration(
                    convertConfigurationToUpdatePayload(editedConfiguration),
                    getResourceFeatureAssociationUpdatePayload(
                      editedResourceFeatureAssociations,
                      benchlingAppId
                    )
                  );
                  notifications.success('Successfully updated configuration');
                } finally {
                  setIsSubmitting(false);
                }
              }}
              className={classNames(css(styles.stickyButton))}
              dataTestAttrs={{component: 'BenchlingAppConfigurationBlock', element: 'submitButton'}}
            />
          </TooltipWrapper>
        </div>
        {isMissingPublicKey && (
          <div className={css(styles.publicKeyWarningBanner)}>
            <InlineBanner
              bannerType={InlineBannerType.WARNING}
              text="Missing public key"
              supportingInfo={
                <span>
                  This app's configuration includes secure_text fields, which require a public key in order to
                  be encrypted. Please{' '}
                  <a target="_blank" rel="noopener noreferrer" href={`/developer/apps/${benchlingAppId}`}>
                    visit the app's overview page
                  </a>{' '}
                  and provide a public key prior to filling in configuration on this page.
                </span>
              }
            />
          </div>
        )}
      </div>

      <BasicConfigurationBlock
        configurations={configurations}
        editedConfiguration={editedConfiguration}
        updateEditedConfiguration={updateEditedConfiguration}
        configsValidation={configsValidation}
        updateConfigsValidation={updateConfigsValidation}
        updateArrayElementValidation={updateArrayElementValidation}
      />
      <FeatureConfigurationBlock
        features={features}
        appName={benchlingAppName}
        appId={benchlingAppId}
        resourceFeatureAssociations={editedResourceFeatureAssociations}
        updateResourceFeatureAssociations={updateResourceFeatureAssociations}
      />
    </>
  );
}

export default function BenchlingAppConfigurationSection({
  benchlingAppId,
}: {
  benchlingAppId: string;
}): JSX.Element {
  const configurationQueryResult = useBenchlingQuery<
    getBenchlingAppConfigurationQuery,
    getBenchlingAppConfigurationQueryVariables
  >(BENCHLING_APP_CONFIGURATION_QUERY, {
    variables: {benchlingAppId},
    fetchPolicy: 'network-only',
  });
  const resourceFeatureAssociationsQuery = useBenchlingQuery<GetResourceFeatureAssociations>(
    RESOURCE_FEATURE_ASSOCIATIONS_QUERY,
    {
      fetchPolicy: 'network-only',
    }
  );
  if (
    configurationQueryResult.type === QueryResultType.PENDING ||
    resourceFeatureAssociationsQuery.type === QueryResultType.PENDING
  ) {
    return <CenteredHorizontalThrobber />;
  }
  if (
    configurationQueryResult.type === QueryResultType.ERROR ||
    resourceFeatureAssociationsQuery.type === QueryResultType.ERROR
  ) {
    notifications.error('Unable to load app configuration. Please try again.');
    return <></>;
  }

  if (isInaccessible(configurationQueryResult.data.benchlingApp)) {
    return <></>;
  }
  const benchlingApp = configurationQueryResult.data.benchlingApp;
  const features = benchlingApp.features.filter(
    (feature) => feature.type === BenchlingAppFeatureType.ASSAY_RUN
  );

  const hasConfigurations = benchlingApp.configuration.length > 0;
  const hasFeatures = features.length > 0;

  return (
    <>
      {benchlingApp.isConfigurationTabVisible ? (
        hasConfigurations || hasFeatures ? (
          <>
            <BenchlingAppConfigurationBlock
              benchlingAppId={benchlingAppId}
              benchlingAppName={benchlingApp.name}
              configurations={benchlingApp.configuration}
              resourceFeatureAssociations={
                resourceFeatureAssociationsQuery.data.benchlingAppResourceFeatureAssociations
              }
              handleUpdateConfiguration={async (
                newConfiguration: Array<Configuration>,
                newResourceFeatureAssociations: Array<{feature: number; resources: Array<string>}>
              ) => {
                await updateBenchlingAppInstance(benchlingAppId, {
                  configuration: newConfiguration,
                  resourceFeatureAssociations: newResourceFeatureAssociations,
                });
                await configurationQueryResult.refetch();
                await resourceFeatureAssociationsQuery.refetch();
              }}
              features={features}
              isMissingPublicKey={benchlingApp.isMissingRequiredPublicKey}
            />
          </>
        ) : (
          <>
            <Header1 className={css(styles.emptyConfigurationsHeader)}>Configurations</Header1>
            <EmptyStateCard
              title="No configurations"
              message="This app has no configuration options to fill out."
            />
          </>
        )
      ) : (
        <div className={classNames('u-lr-split', 'u-spacing-top', css(styles.configurations))}>
          <div>
            <Header1>Configurations</Header1>
            <div>
              Insufficient permissions to view/edit app configuration. Only the app's creator and tenant
              admins may edit app configuration.
            </div>
          </div>
        </div>
      )}
    </>
  );
}
